import moment from 'moment-timezone'

/**
 * Берёт «настенную» дату из Date (Y-M-D как в сетке vue-cal) и строит
 * moment в timezone филиала. Не конвертирует инстант целиком — иначе
 * граница суток уезжает в другой день при расхождении TZ браузера и филиала.
 * Полдень — чтобы не упереться в сдвиг DST.
 */
export function calendarWallMoment(selectedDate, timezone) {
  const d =
    selectedDate instanceof Date
      ? selectedDate
      : new Date(selectedDate || Date.now())
  if (Number.isNaN(d.getTime())) {
    return moment.tz(timezone)
  }
  return moment.tz(
    {
      year: d.getFullYear(),
      month: d.getMonth(),
      day: d.getDate(),
      hour: 12,
      minute: 0,
      second: 0,
    },
    timezone
  )
}

/**
 * Диапазон для запроса записей = только видимый период (день / неделя / месяц).
 * Месяц назад заранее не грузим: при листании, если период уже в recordsLoadedRange —
 * без запроса; если вышли за край — догружаем недостающее.
 */
export function getCalendarRecordsDateRange({
  selectedDate,
  view = 'week',
  timezone = 'Europe/Moscow',
}) {
  const tz = timezone || 'Europe/Moscow'
  const anchor = calendarWallMoment(selectedDate, tz)

  let visibleStart = anchor.clone()
  let visibleEnd = anchor.clone()
  if (view === 'month') {
    visibleStart = anchor.clone().startOf('month')
    visibleEnd = anchor.clone().endOf('month')
  } else if (view === 'week') {
    // isoWeek = пн–вс, как у vue-cal в ЛК (не воскресенье из en-locale moment)
    visibleStart = anchor.clone().startOf('isoWeek')
    visibleEnd = anchor.clone().endOf('isoWeek')
  } else {
    visibleStart = anchor.clone().startOf('day')
    visibleEnd = anchor.clone().endOf('day')
  }

  return {
    from: visibleStart.format('YYYY-MM-DD'),
    to: visibleEnd.format('YYYY-MM-DD'),
  }
}

/** Видимый период выходит за уже загруженный диапазон? */
export function shouldExpandCalendarRecordsRange(loadedRange, nextRange) {
  if (!loadedRange?.from || !loadedRange?.to) {
    return true
  }
  return nextRange.from < loadedRange.from || nextRange.to > loadedRange.to
}

/** Объединение двух диапазонов YYYY-MM-DD. */
export function mergeCalendarRecordsRanges(a, b) {
  if (!a?.from || !a?.to) return { ...b }
  if (!b?.from || !b?.to) return { ...a }
  return {
    from: a.from < b.from ? a.from : b.from,
    to: a.to > b.to ? a.to : b.to,
  }
}

/**
 * isoWeek-ключ (пн YYYY-MM-DD) для даты в TZ филиала.
 * Совпадает с weekKey в business/calendarRecordsByOrgAndWeek.
 */
export function getCalendarIsoWeekKey(
  selectedDate,
  timezone = 'Europe/Moscow'
) {
  return calendarWallMoment(selectedDate, timezone || 'Europe/Moscow')
    .startOf('isoWeek')
    .format('YYYY-MM-DD')
}

/**
 * Диапазон пн–вс для выбранной даты (как у календаря в week view).
 */
export function getCalendarIsoWeekRange(
  selectedDate,
  timezone = 'Europe/Moscow'
) {
  const start = calendarWallMoment(
    selectedDate,
    timezone || 'Europe/Moscow'
  ).startOf('isoWeek')
  return {
    from: start.format('YYYY-MM-DD'),
    to: start.clone().endOf('isoWeek').format('YYYY-MM-DD'),
  }
}

/**
 * Раскладывает raw calendar-records по isoWeek-ключам внутри fetchRange.
 * Пустые недели тоже пишутся — чтобы сайд-панель не ходила в API повторно.
 */
export function groupCalendarRecordsByIsoWeek(
  records,
  fetchRange,
  timezone = 'Europe/Moscow'
) {
  const tz = timezone || 'Europe/Moscow'
  const byWeek = {}
  if (!fetchRange?.from || !fetchRange?.to) return byWeek

  const cursor = moment.tz(fetchRange.from, 'YYYY-MM-DD', tz).startOf('isoWeek')
  const last = moment.tz(fetchRange.to, 'YYYY-MM-DD', tz).startOf('isoWeek')

  while (cursor.isSameOrBefore(last, 'day')) {
    byWeek[cursor.format('YYYY-MM-DD')] = []
    cursor.add(1, 'week')
  }

  for (const record of records || []) {
    const day = moment.utc(record.start).tz(tz)
    if (!day.isValid()) continue
    const weekKey = day.clone().startOf('isoWeek').format('YYYY-MM-DD')
    if (!Object.prototype.hasOwnProperty.call(byWeek, weekKey)) continue
    byWeek[weekKey].push(record)
  }

  return byWeek
}
